import { useEffect } from 'react';
import {
  Building,
  Users,
  HardDrive,
  Bell,
  CheckCircle,
  Calendar,
  Infinity,
  Tag,
  XCircle,
} from 'lucide-react';
import type { PlanStepProps } from '../types';

const GB_IN_BYTES = 1073741824;

const featureLabels: Record<string, string> = {
  basicReports: 'Reportes básicos',
  advancedReports: 'Reportes avanzados',
  apiAccess: 'Acceso API',
  customBranding: 'Personalización de marca',
  multipleAdmins: 'Múltiples administradores',
  prioritySupport: 'Soporte prioritario 24/7',
  autoBackup: 'Backup automático',
  multiLanguage: 'Multi-idioma',
  mobileAccess: 'Acceso móvil',
  unlimitedIntegrations: 'Integraciones ilimitadas',
};

export function SummaryStep({ data, onValidChange }: PlanStepProps) {
  useEffect(() => {
    onValidChange(true);
  }, [onValidChange]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(price);
  };

  const storageGb = Math.round(data.includedStorageBytes / GB_IN_BYTES);
  const features = data.features ?? {};
  const featureEntries = Object.entries(features);
  const enabledFeatures = featureEntries.filter(([, v]) => v);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Resumen del Plan
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Revisa la configuración antes de guardar
        </p>
      </div>

      {/* Plan Card Preview */}
      <div className="relative p-6 bg-gradient-to-br from-brand-50 to-brand-100 dark:from-brand-900/20 dark:to-brand-900/10 rounded-2xl overflow-hidden">
        <div className="flex items-center gap-2 mb-2">
          <h4 className="text-2xl font-bold text-gray-900 dark:text-white">
            {data.name || 'Sin nombre'}
          </h4>
          <span className="px-2 py-0.5 bg-brand-500 text-white text-xs font-medium rounded uppercase">
            {data.code || 'CODE'}
          </span>
        </div>

        {data.description && (
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {data.description}
          </p>
        )}

        <div className="flex items-baseline gap-1 mb-2">
          <span className="text-4xl font-bold text-gray-900 dark:text-white">
            {formatPrice(data.basePrice)}
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            /{data.billingCycle === 'MONTHLY' ? 'mes' : 'año'}
          </span>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            Facturación {data.billingCycle === 'MONTHLY' ? 'mensual' : 'anual'}
          </div>
          {data.trialDays > 0 && (
            <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <Tag className="w-4 h-4" />
              {data.trialDays} días de prueba
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          {data.isActive && (
            <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium rounded">
              Activo
            </span>
          )}
          {data.isPublic && (
            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-medium rounded">
              Público
            </span>
          )}
        </div>
      </div>

      {/* Limits */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-white mb-4 uppercase tracking-wide">
          Límites Incluidos
        </h4>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="p-2 bg-white dark:bg-gray-700 rounded-lg inline-block mb-2">
              <Building className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </div>
            <p className="text-lg font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-1">
              {data.includedUnits === -1 ? <Infinity className="w-5 h-5" /> : data.includedUnits}
            </p>
            <p className="text-xs text-gray-500">Unidades</p>
          </div>
          <div className="text-center">
            <div className="p-2 bg-white dark:bg-gray-700 rounded-lg inline-block mb-2">
              <Users className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </div>
            <p className="text-lg font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-1">
              {data.includedUsers === -1 ? <Infinity className="w-5 h-5" /> : data.includedUsers}
            </p>
            <p className="text-xs text-gray-500">Usuarios</p>
          </div>
          <div className="text-center">
            <div className="p-2 bg-white dark:bg-gray-700 rounded-lg inline-block mb-2">
              <HardDrive className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </div>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {storageGb} GB
            </p>
            <p className="text-xs text-gray-500">Almacenamiento</p>
          </div>
          <div className="text-center">
            <div className="p-2 bg-white dark:bg-gray-700 rounded-lg inline-block mb-2">
              <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </div>
            <p className="text-lg font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-1">
              {data.includedNotifications === -1 ? <Infinity className="w-5 h-5" /> : data.includedNotifications.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500">Notificaciones</p>
          </div>
        </div>
      </div>

      {/* Extra Prices */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-white mb-4 uppercase tracking-wide">
          Precios por Excedente
        </h4>
        <div className="grid grid-cols-4 gap-4 text-center text-sm">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">${data.extraUnitPrice}</p>
            <p className="text-xs text-gray-500">Por unidad</p>
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">${data.extraUserPrice}</p>
            <p className="text-xs text-gray-500">Por usuario</p>
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">${data.extraStoragePricePerGb}</p>
            <p className="text-xs text-gray-500">Por GB</p>
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">${data.extraNotificationPrice}</p>
            <p className="text-xs text-gray-500">Por notif.</p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-white mb-4 uppercase tracking-wide">
          Características ({enabledFeatures.length})
        </h4>
        {featureEntries.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            No se han agregado características
          </p>
        ) : (
          <div className="space-y-2">
            {featureEntries.map(([key, enabled]) => (
              <div key={key} className="flex items-center gap-2">
                {enabled ? (
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                )}
                <span className={`text-sm ${enabled ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 line-through'}`}>
                  {featureLabels[key] || key}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation */}
      <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-emerald-800 dark:text-emerald-200">
              Listo para guardar
            </h4>
            <p className="text-sm text-emerald-600 dark:text-emerald-300 mt-1">
              Haz clic en "Guardar" para crear el plan. Podrás editarlo después si es necesario.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
